import Q from 'q';
import request from 'superagent';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import {
  createPaymentMethod,
  createStripeTokenFromElement,
  getDeviceInfo,
  logException,
  trimValues,
} from 'shared/lib/utils';
import * as analytics from 'shared/lib/analytics';
import * as authApi from './auth';
import * as baggageApi from './baggage';
import * as bopApi from './bop';
import * as collectiblesApi from './collectibles';
import * as collectiblesEndorsementsApi from './collectiblesEndorsements';
import * as miscApi from './misc';
import * as rentalCarApi from './rentalCar';
import * as rentersApi from './renters';
import * as sharedApi from 'shared/lib/api';

/**
 * Purchase a product.
 *
 * @param {Object} opts The options.
 * @param {String} [opts.authToken] The Sure API auth token.
 * @param {Object} opts.cardElement The Stripe card element.
 * @param {String} [opts.cardName] The card holder name.
 * @param {String} [opts.cardAddressLine1] The card holder address line 1.
 * @param {String} [opts.cardAddressCity] The card holder city.
 * @param {String} [opts.cardAddressState] The card holder state.
 * @param {String} opts.planId The selected plan ID.
 * @param {String} [opts.planTitle] The plan title.
 * @param {String} opts.firstName The user's first name.
 * @param {String} opts.lastName The user's last name.
 * @param {String} opts.email The user's email.
 * @param {String} opts.phoneNumber The user's phone number.
 * @param {Number} [opts.lat] The user's lat location coordinate.
 * @param {Number} [opts.lng] The user's lng location coordinate.
 * @param {Number} opts.revenue The total revenue of the purchase.
 * @param {Object} opts.payload The purchase payload.
 * @param {String} opts.endpoint The API endpoint for the purchase.
 * @param {String} opts.prefix The events prefix.
 * @param {String} opts.verticalCode The vertical code.
 * @param {String} opts.verticalName The vertical name.
 * @param {String} [opts.paymentCadence] The payment cadence ("Yearly", "Monthly", "Once").
 * @param {String} [opts.suffix] The events suffix.
 * @param {String} [opts.uid] A unique order ID.
 *   This parameter will be used as an idempotency header for the request.
 *   If not specified, it will use the agreement ID returned from the sucessful
 *   purchase payload for analytics.
 */
export function purchase(opts) {
  const deferred = Q.defer();
  opts.payload = opts.payload || {};
  opts.prefix = opts.prefix || '';
  opts.suffix = opts.suffix || '';
  opts.cardName = opts.cardName || '';
  opts.cardAddressLine1 = opts.cardAddressLine1 || '';
  opts.cardAddressCity = opts.cardAddressCity || '';
  opts.cardAddressState = opts.cardAddressState || '';
  opts = trimValues(opts);
  analytics.track(`${opts.prefix} - Purchase Initiated${opts.suffix}`, {
    category: 'order',
    label: opts.planId,
    value: opts.revenue,
  });

  new Promise((resolve, reject) => {
    if (opts.cardElement) {
      createStripeTokenFromElement(
        opts.cardElement,
        opts.cardName,
        opts.cardAddressLine1,
        opts.cardAddressCity,
        opts.cardAddressState
      )
        .then(stripeToken => {
          analytics.track(
            `${opts.prefix} - Stripe Token Created${opts.suffix}`,
            {
              category: 'order',
              nonInteraction: 1,
            }
          );

          let apiEndpoint;

          if (opts.authToken) {
            apiEndpoint = `${
              process.env.REACT_APP_SURE_API_BASE_URL
            }/api/user/v2/accounts/payment-method/add`;
          } else {
            // Renters V3
            if (opts.verticalCode === 'renters') {
              apiEndpoint = `${
                process.env.REACT_APP_API_V2
              }/connect/api/user/v3/potential/add-method/`;
            } else {
              apiEndpoint = `${
                process.env.REACT_APP_SURE_API_BASE_URL
              }/api/user/v2/potential/add-method`;
            }
          }

          return createPaymentMethod({
            firstName: opts.firstName,
            lastName: opts.lastName,
            email: opts.email,
            getId: true,
            authToken: opts.authToken,
            apiEndpoint,
            stripeToken,
          });
        })
        .then(resolve)
        .catch(reject);
    } else {
      resolve();
    }
  })
    .then(paymentMethodId => {
      analytics.track(`${opts.prefix} - Payment Method Created${opts.suffix}`, {
        category: 'order',
        value: opts.authToken ? 1 : 0,
        nonInteraction: 1,
      });

      const apiDeferred = Q.defer();
      const browserEnv = {
        lat: opts.lat,
        lng: opts.lng,
        ...getDeviceInfo(),
      };

      if (!opts.authToken) {
        opts.payload.first_name = opts.firstName;
        opts.payload.last_name = opts.lastName;
        opts.payload.phone_number = opts.phoneNumber;
        opts.payload.email = opts.email;
      }

      const req = request
        .post(`${process.env.REACT_APP_SURE_API_BASE_URL}${opts.endpoint}`)
        .set('X-Environment', JSON.stringify(browserEnv));

      // for translation layer compatibility, authToken can sometimes be null. Bearer null throws an error in Platform.
      if (opts.authToken) {
        req.set('Authorization', `Bearer ${opts.authToken}`);
      }

      if (opts.uid) {
        req.set('X-Unique-Key', opts.uid);
      }

      req
        .send({
          payment_method_id: paymentMethodId,
          plan_id: opts.planId,
          ...opts.payload,
        })
        .end((error, response) => {
          if (error) {
            logException(error, {
              response: get(response, 'body'),
              payload: opts.payload,
            });
            apiDeferred.reject(error);
          } else {
            apiDeferred.resolve(response.body);
          }
        });

      return apiDeferred.promise;
    })
    .then((purchaseData = {}) => {
      if (isEmpty(purchaseData) || !isPlainObject(purchaseData)) {
        purchaseData = {};
      }

      if (purchaseData.account_id) {
        analytics.track('Dormant Account Created', {
          category: 'user',
          label: purchaseData.account_id,
        });
        analytics.alias(purchaseData.account_id);
      }
      analytics.track(`${opts.prefix} - Purchase Completed${opts.suffix}`, {
        category: 'order',
        label: opts.planId,
        value: opts.revenue,
        nonInteraction: 1,
      });
      analytics.track('Order Completed', {
        category: 'order',
        order_id: opts.uid || purchaseData.agreement_id,
        revenue: opts.revenue,
        email: opts.email,
        name: opts.payload.first_name,
        surname: opts.payload.last_name,
      });
      analytics.trackGTMPurchase({
        orderId: opts.uid || purchaseData.agreement_id,
        revenue: opts.revenue,
        paymentCadence: opts.paymentCadence,
        planTitle: opts.planTitle,
        verticalCode: opts.verticalCode,
        verticalName: opts.verticalName,
      });

      const hydratedPurchaseData = Object.assign(
        {
          _payload: opts.payload,
        },
        purchaseData
      );

      deferred.resolve(hydratedPurchaseData);
      return hydratedPurchaseData;
    })
    .catch(error => {
      logException(error);
      analytics.track(`${opts.prefix} - Purchase Failed${opts.suffix}`, {
        category: 'order',
        label: opts.planId,
        value: opts.revenue,
        nonInteraction: 1,
      });
      deferred.reject(error);
    });

  return deferred.promise;
}

const api = {
  /**
   * Update a user's account information.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {String} opts.firstName The user's first name.
   * @param {String} opts.middleName The user's middle name.
   * @param {String} opts.lastName The user's last name.
   * @param {String} opts.phoneNumber The user's phone number.
   * @param {String} opts.beneficiaryFirstName The beneficiary's first name.
   * @param {String} opts.beneficiaryLastName The beneficiary's last name.
   * @param {String} opts.beneficiaryEmail The beneficiary's email.
   * @param {String} opts.countryId The user's residence country ID.
   * @param {String} opts.regionId The user's residence region ID.
   */
  updateAccountInfo(opts) {
    const payload = trimValues({
      first_name: opts.firstName,
      middle_name: opts.middleName,
      last_name: opts.lastName,
      phone_number: opts.phoneNumber,
      beneficiary_first_name: opts.beneficiaryFirstName,
      beneficiary_last_name: opts.beneficiaryLastName,
      beneficiary_email: opts.beneficiaryEmail,
      country_id: opts.countryId,
      region_id: opts.regionId,
    });

    return fetch(
      `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/accounts/info`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${opts.authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, {
            payload,
            response: errorResp,
          });
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /**
   * Add an address for a user.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {Object} opts.line1 The street address.
   * @param {Object} [opts.line2] The unit number.
   * @param {Object} opts.city The city.
   * @param {Object} opts.region The state or region.
   * @param {Object} opts.country The country.
   * @param {Object} opts.postal The postal code.
   * @param {Number} opts.lat The user's lat location coordinate.
   * @param {Number} opts.lng The user's lng location coordinate.
   */
  createAddress(opts) {
    const deferred = Q.defer();

    const payload = trimValues({
      line1: opts.line1,
      line2: opts.line2,
      city: opts.city,
      state_or_region: opts.region,
      country: opts.country,
      postal: opts.postal,
      latitude: opts.lat,
      longitude: opts.lng,
    });

    request
      .post(
        `${
          process.env.REACT_APP_SURE_API_BASE_URL
        }/api/user/v2/accounts/address`
      )
      .set('Authorization', `Bearer ${opts.authToken}`)
      .send(payload)
      .end((error, response) => {
        if (error) {
          logException(error, { response: response.body, payload });
          deferred.reject(error.response.body.error);
        } else {
          deferred.resolve(response.body);
        }
      });

    return deferred.promise;
  },

  /**
   * Get user's policies summaries grouped by vertical.
   * Policies returned from this call contain limited details.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   */
  getAllPolicies(opts = {}) {
    const headers = {
      Authorization: `Bearer ${opts.authToken}`,
      'X-Environment': JSON.stringify(getDeviceInfo()),
    };

    return fetch(
      `${
        process.env.REACT_APP_SURE_API_BASE_URL
      }/api/user/v2/policies/all-verticals`,

      {
        headers,
      }
    )
      .then(response => {
        if (response.status >= 400) {
          return response.json().then(errorResp => {
            const error = new Error(response.statusText);
            logException(error, errorResp);
            throw error;
          });
        } else {
          return response.json();
        }
      })
      .catch(err => {
        console.warn(err.message);
      });
  },

  /**
   * Get user's policies grouped by vertical.
   * Policies returned from this call contain limited details.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {String} opts.verticalCode The vertical code to fetch policies for.
   */
  getPoliciesForVertical(opts = {}) {
    return fetch(
      `${
        process.env.REACT_APP_SURE_API_BASE_URL
      }/api/user/v2/policies/vertical/${opts.verticalCode}`,
      {
        headers: {
          Authorization: `Bearer ${opts.authToken}`,
          'X-Environment': JSON.stringify(getDeviceInfo()),
        },
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, errorResp);
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /**
   * Get a full policy object by ID.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {String} opts.policyId The policy ID to fetch.
   */
  getPolicyById(opts = {}) {
    const headers = {
      Authorization: `Bearer ${opts.authToken}`,
      'X-Environment': JSON.stringify(getDeviceInfo()),
    };

    return fetch(
      `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
        opts.policyId
      }`,

      {
        headers,
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, errorResp);
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /**
   * Get a checkout for canceling a policy.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {String} opts.policyId The policy ID.
   */
  getCancelPolicyCheckout(opts) {
    return fetch(
      `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
        opts.policyId
      }/cancel/checkout`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${opts.authToken}`,
          'Content-Type': 'application/json',
        },
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, {
            policyId: opts.policyId,
            response: errorResp,
          });
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /**
   * Submit a checkout for canceling a policy.
   *
   * @param {Object} opts The options.
   * @param {String} opts.authToken The Sure API auth token.
   * @param {String} opts.policyId The policy ID.
   */
  submitCancelPolicy(opts) {
    return fetch(
      `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
        opts.policyId
      }/cancel/submit`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${opts.authToken}`,
          'Content-Type': 'application/json',
        },
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, {
            policyId: opts.policyId,
            response: errorResp,
          });
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /*
   * Get collectibles categories.
   */
  getCollectiblesCategories({ authToken } = {}) {
    if (process.env.REACT_APP_IS_LEGACY === 'false') {
      return new Promise(resolve => {
        resolve('ok');
      });
    }
    return fetch(
      `${
        process.env.REACT_APP_SURE_API_BASE_URL
      }/api/user/v2/protections/collectibles/categories`,
      {
        headers: {
          'X-Environment': JSON.stringify(getDeviceInfo()),
          Authorization: `Bearer ${authToken}`,
        },
      }
    ).then(response => {
      if (response.status >= 400) {
        return response.json().then(errorResp => {
          const error = new Error(response.statusText);
          logException(error, errorResp);
          throw error;
        });
      } else {
        return response.json();
      }
    });
  },

  /**
   * Get payment cadence options for a quote.
   *
   * @param {String} [opts.authToken] The Sure API auth token.
   * @param {String} opts.quoteId The quote ID.
   * @param {Object} opts.propertyAddress The user's property address.
   * @param {Object} opts.propertyAddress.street_address The street address.
   * @param {Object} [opts.propertyAddress.unit] The unit number.
   * @param {Object} opts.propertyAddress.city The city.
   * @param {Object} opts.propertyAddress.region The state or region.
   * @param {Object} opts.propertyAddress.country The country.
   * @param {Object} opts.propertyAddress.postal The postal code.
   */
  getRentersPaymentCadences(opts = {}) {
    const deferred = Q.defer();
    const payload = {
      property_address: opts.property_address,
      quote_id: opts.quoteId,
    };

    request
      .post(
        `${
          process.env.REACT_APP_SURE_API_BASE_URL
        }/api/user/v2/protections/renters/cadences`
      )
      .set('Authorization', `Bearer ${opts.authToken}`)
      .set('X-Environment', JSON.stringify(getDeviceInfo()))
      .send(payload)
      .end((error, response) => {
        if (error) {
          logException(error, { payload });
          deferred.reject(error.response.body.error);
        } else {
          deferred.resolve(response.body);
        }
      });

    return deferred.promise;
  },

  ...authApi,
  ...baggageApi,
  ...bopApi,
  ...collectiblesApi,
  ...collectiblesEndorsementsApi,
  ...miscApi,
  ...rentalCarApi,
  ...rentersApi,
  ...sharedApi,
};

export default api;
